import React, { useState, useMemo, useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import { notificationApi } from '@bingads-webui-campaign/notification-center';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { StepWrapper } from '../../components';
import { CampaignSelectionMode } from './campaign-selection-mode';
import { SpecificCampaignSelection } from './specific-campaign-selection';
import { ApplyingChanges } from './applying-changes';
import { CAMPAIGN_SELECTION_MODES } from './constants';
import { useBulkSession, useAutoBiddingFac } from '../../hooks';

const CAMPAIGN_SELECTION_STEPS = Object.freeze({
  SELECTION_MODE: 0,
  SPECIFIC_CAMPAIGN_SELECTION: 1,
  APPLYING_CHANGES: 2,
});

export const CampaignAutobiddingSelection = (props) => {
  const {
    coupon,
    selectedCampaigns,
    setSelectedCampaigns,
    onNext,
    onClose,
  } = props;

  const i18n = useContext(I18nContext);
  const { updateCampaigns } = useBulkSession(coupon);
  const { adoptCoupon } = useAutoBiddingFac();

  const [currentStep, setCurrentStep] = useState(CAMPAIGN_SELECTION_STEPS.SELECTION_MODE);
  const [campaignSelectionMode, setCampaignSelectionMode] = useState(CAMPAIGN_SELECTION_MODES.ALL);

  const applyChangesToCampaigns = useCallback(() => {
    setCurrentStep(CAMPAIGN_SELECTION_STEPS.APPLYING_CHANGES);

    return updateCampaigns(selectedCampaigns)
      .then(() => adoptCoupon(coupon))
      .then(onNext)
      .catch(() => {
        setCurrentStep(CAMPAIGN_SELECTION_STEPS.SPECIFIC_CAMPAIGN_SELECTION);
        notificationApi.showWarning(i18n.getString(_TL_('Microsoft Advertising problem, try again later.')));
      });
  }, [adoptCoupon, coupon, i18n, onNext, selectedCampaigns, updateCampaigns]);

  const campaignSelectionContent = useMemo(() => {
    switch (currentStep) {
      case CAMPAIGN_SELECTION_STEPS.SELECTION_MODE:
        return (
          <StepWrapper coupon={coupon} onClose={onClose}>
            <div className="campaign-selection">
              <CampaignSelectionMode
                coupon={coupon}
                onClose={onClose}
                goToSpecificCampaignSelection={() => setCurrentStep(CAMPAIGN_SELECTION_STEPS.SPECIFIC_CAMPAIGN_SELECTION)}
                applyChangesToAllCampaigns={applyChangesToCampaigns}
                goToConfirmationStep={onNext}
                campaignSelectionMode={campaignSelectionMode}
                setCampaignSelectionMode={setCampaignSelectionMode}
              />
            </div>
          </StepWrapper>
        );
      case CAMPAIGN_SELECTION_STEPS.SPECIFIC_CAMPAIGN_SELECTION:
        return (
          <StepWrapper coupon={coupon} onClose={onClose}>
            <div className="campaign-selection">
              <SpecificCampaignSelection
                coupon={coupon}
                onClose={onClose}
                selectedCampaigns={selectedCampaigns}
                setSelectedCampaigns={setSelectedCampaigns}
                onNext={applyChangesToCampaigns}
                onBack={() => setCurrentStep(CAMPAIGN_SELECTION_STEPS.SELECTION_MODE)}
              />
            </div>
          </StepWrapper>
        );
      case CAMPAIGN_SELECTION_STEPS.APPLYING_CHANGES:
        return (
          <div className="campaign-selection">
            <ApplyingChanges />
          </div>
        );
      default:
        return null;
    }
  }, [applyChangesToCampaigns, campaignSelectionMode, coupon, currentStep, onClose, onNext, selectedCampaigns, setSelectedCampaigns]);

  return (
    <>
      {campaignSelectionContent}
    </>
  );
};

CampaignAutobiddingSelection.propTypes = {
  coupon: PropTypes.shape({
    FeatureId: PropTypes.number.isRequired,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  selectedCampaigns: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  setSelectedCampaigns: PropTypes.func.isRequired,
};
